import React, { useCallback, useEffect, useState } from "react";
import { api, formatDateBR, formatCurrency, todayISO } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer, Check, X, ClipboardList, CheckCircle2 } from "lucide-react";

const VEHICLE_TYPES = [
    { value: "onibus", label: "Ônibus" },
    { value: "caminhonete", label: "Caminhonete" },
    { value: "carro", label: "Carro" },
    { value: "van", label: "Van" },
    { value: "outro", label: "Outro" },
];

const emptyRequest = {
    solicitante_nome: "", entidade: "", telefone: "",
    origem: "", destino: "", data_viagem: todayISO(),
    datas: [todayISO()],
    motivo: "",
    tipo_veiculo: "carro", veiculo_placa: "", motorista_nome: "",
    num_passageiros: 1, dias: 1,
    valor_estimado: "", chave_pix: "", vinculo: "outro", observacoes: "",
};

const VINCULOS = [
    { value: "cooperado", label: "Cooperado" },
    { value: "nao_cooperado", label: "Não cooperado" },
    { value: "setor_social", label: "Setor Social (COOMFLONA)" },
    { value: "entidade", label: "Entidade externa" },
    { value: "comunidade", label: "Comunidade" },
    { value: "outro", label: "Outro" },
];
const VINCULO_LABELS = Object.fromEntries(VINCULOS.map((v) => [v.value, v.label]));

const statusStyles = {
    pendente: { label: "Pendente", cls: "bg-yellow-100 text-yellow-900 border-yellow-300" },
    aprovada: { label: "Aprovada", cls: "bg-primary/15 text-primary border-primary/30" },
    negada: { label: "Negada", cls: "bg-red-100 text-red-800 border-red-300" },
    concluida: { label: "Concluída", cls: "bg-green-100 text-green-800 border-green-300" },
};

function StatusBadge({ status }) {
    const s = statusStyles[status] || statusStyles.pendente;
    return <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase font-semibold rounded border ${s.cls}`}>{s.label}</span>;
}

export default function Requests() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("todos");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyRequest);
    const [editingId, setEditingId] = useState(null);
    const [approveDialog, setApproveDialog] = useState(null);
    const [approveForm, setApproveForm] = useState({
        observacoes_decisao: "",
        criar_viagem: false,
        tipo_viagem: "diaria",
        responsavel_nome: "", cpf: "",
        veiculo_modelo: "", veiculo_placa: "", valor: "",
    });
    const [rejectDialog, setRejectDialog] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (filter !== "todos") params.status = filter;
        const res = await api.get("/requests", { params });
        setRequests(res.data);
        setLoading(false);
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm({ ...emptyRequest, data_viagem: todayISO() }); setEditingId(null); setOpen(true); };

    const openEdit = (r) => {
        setForm({
            solicitante_nome: r.solicitante_nome, entidade: r.entidade || "",
            telefone: r.telefone || "",
            origem: r.origem || "",
            destino: r.destino,
            data_viagem: r.data_viagem,
            datas: r.datas && r.datas.length ? r.datas : [r.data_viagem],
            motivo: r.motivo,
            tipo_veiculo: r.tipo_veiculo, num_passageiros: r.num_passageiros || 1,
            veiculo_placa: r.veiculo_placa || "",
            motorista_nome: r.motorista_nome || "",
            dias: r.dias || 1,
            valor_estimado: r.valor_estimado ?? "",
            chave_pix: r.chave_pix || "",
            vinculo: r.vinculo || "outro",
            observacoes: r.observacoes || "",
        });
        setEditingId(r.id);
        setOpen(true);
    };

    // Helpers para múltiplas datas
    const updateDatas = (novasDatas) => {
        const validas = novasDatas.filter(Boolean);
        const diasNum = Math.max(1, validas.length);
        setForm((f) => ({ ...f, datas: novasDatas, dias: diasNum, data_viagem: validas[0] || f.data_viagem }));
    };
    const addData = () => updateDatas([...(form.datas || []), ""]);
    const removeData = (idx) => updateDatas((form.datas || []).filter((_, i) => i !== idx));
    const changeData = (idx, val) => updateDatas((form.datas || []).map((d, i) => i === idx ? val : d));

    const save = async () => {
        if (!form.solicitante_nome.trim() || !form.destino.trim() || !form.motivo.trim()) {
            toast.error("Preencha nome do solicitante, destino e motivo");
            return;
        }
        const validas = (form.datas || []).filter(Boolean);
        if (validas.length === 0) {
            toast.error("Adicione pelo menos uma data");
            return;
        }
        const payload = {
            ...form,
            data_viagem: validas[0],
            datas: validas,
            num_passageiros: parseInt(form.num_passageiros) || 1,
            dias: validas.length,
            valor_estimado: form.valor_estimado === "" || form.valor_estimado === null ? 0 : parseFloat(form.valor_estimado),
        };
        try {
            if (editingId) {
                await api.put(`/requests/${editingId}`, payload);
                toast.success("Solicitação atualizada!");
            } else {
                await api.post("/requests", payload);
                toast.success("Solicitação registrada!");
            }
            setOpen(false);
            load();
        } catch {
            toast.error("Erro ao salvar");
        }
    };

    const openApprove = (r) => {
        setApproveDialog(r);
        setApproveForm({
            observacoes_decisao: "",
            criar_viagem: false,
            tipo_viagem: "diaria",
            responsavel_nome: "", cpf: "",
            veiculo_modelo: "", veiculo_placa: "", valor: "",
        });
    };

    const doApprove = async () => {
        try {
            const payload = {
                ...approveForm,
                valor: approveForm.valor ? parseFloat(approveForm.valor) : 0,
            };
            if (payload.criar_viagem && (!payload.responsavel_nome || !payload.veiculo_placa)) {
                toast.error("Para criar a viagem, informe responsável e placa");
                return;
            }
            await api.post(`/requests/${approveDialog.id}/approve`, payload);
            toast.success(payload.criar_viagem ? "Aprovada e viagem criada!" : "Solicitação aprovada!");
            setApproveDialog(null);
            load();
        } catch (err) {
            const msg = err.response?.data?.detail || "Erro ao aprovar";
            toast.error(typeof msg === "string" ? msg : "Erro ao aprovar");
        }
    };

    const doReject = async () => {
        try {
            await api.post(`/requests/${rejectDialog.id}/reject`, { observacoes_decisao: rejectReason });
            toast.success("Solicitação negada");
            setRejectDialog(null);
            setRejectReason("");
            load();
        } catch (err) {
            const msg = err.response?.data?.detail || "Erro ao negar";
            toast.error(typeof msg === "string" ? msg : "Erro ao negar");
        }
    };

    const markComplete = async (r) => {
        try {
            await api.post(`/requests/${r.id}/complete`);
            toast.success("Solicitação concluída!");
            load();
        } catch (err) {
            const msg = err.response?.data?.detail || "Erro";
            toast.error(typeof msg === "string" ? msg : "Erro");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/requests/${id}`);
            toast.success("Solicitação removida");
            load();
        } catch {
            toast.error("Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="requests-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                        <ClipboardList className="w-7 h-7 text-primary" /> Solicitações de Transporte
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Pedidos de apoio feitos por comunidades, entidades e cooperados</p>
                </div>
                <Button onClick={openCreate} data-testid="btn-new-request" className="gap-2">
                    <Plus className="w-4 h-4" /> Nova solicitação
                </Button>
            </div>

            <Tabs value={filter} onValueChange={setFilter}>
                <TabsList data-testid="requests-tabs">
                    <TabsTrigger value="todos">Todas</TabsTrigger>
                    <TabsTrigger value="pendente">Pendentes</TabsTrigger>
                    <TabsTrigger value="aprovada">Aprovadas</TabsTrigger>
                    <TabsTrigger value="concluida">Concluídas</TabsTrigger>
                    <TabsTrigger value="negada">Negadas</TabsTrigger>
                </TabsList>
            </Tabs>

            <Card>
                <CardContent className="p-6">
                    {loading ? (
                        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
                    ) : requests.length === 0 ? (
                        <div className="py-14 text-center border-2 border-dashed border-border rounded-xl">
                            <ClipboardList className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 font-semibold">Nenhuma solicitação</div>
                            <div className="text-sm text-muted-foreground mt-1">Clique em "Nova solicitação" para registrar</div>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="requests-list">
                            {requests.map((r) => (
                                <div key={r.id} className="border border-border rounded-xl p-4 hover:shadow-md transition-shadow bg-white">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="flex-1 min-w-[250px]">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <h3 className="font-bold text-foreground">{r.solicitante_nome}</h3>
                                                <StatusBadge status={r.status} />
                                                <Badge variant="secondary" className="text-[10px] uppercase">
                                                    {VEHICLE_TYPES.find((v) => v.value === r.tipo_veiculo)?.label || r.tipo_veiculo}
                                                </Badge>
                                            </div>
                                            {r.entidade && <div className="text-sm text-muted-foreground">{r.entidade}</div>}
                                            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                                                <div>
                                                    <span className="text-muted-foreground">Trajeto:</span>{" "}
                                                    {r.origem && <><strong>{r.origem}</strong> → </>}
                                                    <strong>{r.destino}</strong>
                                                </div>
                                                <div>
                                                    <span className="text-muted-foreground">Data{r.datas && r.datas.length > 1 ? "s" : ""}:</span>{" "}
                                                    <strong>{formatDateBR(r.data_viagem)}</strong>
                                                    {r.datas && r.datas.length > 1 && (
                                                        <span className="text-xs text-muted-foreground"> +{r.datas.length - 1}</span>
                                                    )}
                                                </div>
                                                <div><span className="text-muted-foreground">Dias:</span> <strong>{r.dias || 1}</strong></div>
                                                <div><span className="text-muted-foreground">Passageiros:</span> <strong>{r.num_passageiros}</strong></div>
                                            </div>
                                            {r.vinculo && r.vinculo !== "outro" && (
                                                <div className="mt-1 text-xs">
                                                    <span className="text-muted-foreground">Vínculo:</span>{" "}
                                                    <span className="inline-block px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">{VINCULO_LABELS[r.vinculo] || r.vinculo}</span>
                                                </div>
                                            )}
                                            <div className="mt-2 text-sm"><span className="text-muted-foreground">Motivo:</span> {r.motivo}</div>
                                            {(r.veiculo_placa || r.motorista_nome) && (
                                                <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                                                    {r.veiculo_placa && (
                                                        <div><span className="text-muted-foreground">Placa:</span> <strong className="font-mono">{r.veiculo_placa}</strong></div>
                                                    )}
                                                    {r.motorista_nome && (
                                                        <div><span className="text-muted-foreground">Motorista:</span> <strong>{r.motorista_nome}</strong></div>
                                                    )}
                                                </div>
                                            )}
                                            <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                                                {r.valor_estimado > 0 && (
                                                    <div><span className="text-muted-foreground">Valor estimado:</span> <strong className="text-primary">{formatCurrency(r.valor_estimado)}</strong></div>
                                                )}
                                                {r.chave_pix && (
                                                    <div><span className="text-muted-foreground">PIX:</span> <strong className="font-mono text-xs">{r.chave_pix}</strong></div>
                                                )}
                                            </div>
                                            {r.telefone && <div className="mt-1 text-xs text-muted-foreground">Tel: {r.telefone}</div>}
                                            {r.observacoes_decisao && (
                                                <div className="mt-2 text-xs italic text-muted-foreground border-l-2 border-border pl-2">
                                                    <strong>{r.decidido_por}:</strong> {r.observacoes_decisao}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex gap-1 flex-wrap">
                                            <Button variant="outline" size="sm" onClick={() => window.open(`/solicitacao/${r.id}/imprimir`, "_blank")} title="Imprimir" data-testid={`btn-print-req-${r.id}`}>
                                                <Printer className="w-4 h-4" />
                                            </Button>
                                            {(isAdmin || r.status === "pendente") && (
                                                <Button variant="outline" size="sm" onClick={() => openEdit(r)} title="Editar dados" data-testid={`btn-edit-req-${r.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {r.status === "pendente" && isAdmin && (
                                                <>
                                                    <Button variant="outline" size="sm" className="text-primary border-primary/30" onClick={() => openApprove(r)} title="Aprovar" data-testid={`btn-approve-${r.id}`}>
                                                        <Check className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="outline" size="sm" className="text-destructive border-destructive/30" onClick={() => setRejectDialog(r)} title="Negar" data-testid={`btn-reject-${r.id}`}>
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            )}
                                            {r.status === "aprovada" && (
                                                <Button variant="outline" size="sm" className="text-green-700 border-green-300" onClick={() => markComplete(r)} data-testid={`btn-complete-${r.id}`}>
                                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Concluir
                                                </Button>
                                            )}
                                            {isAdmin && (
                                                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(r)} title="Remover" data-testid={`btn-delete-req-${r.id}`}>
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl" data-testid="request-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Editar solicitação" : "Nova solicitação de transporte"}</DialogTitle>
                        <DialogDescription>Registre o pedido feito pela entidade/comunidade/cooperado</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Nome do solicitante *</Label>
                                <Input value={form.solicitante_nome} onChange={(e) => setForm({ ...form, solicitante_nome: e.target.value })} data-testid="input-req-nome" />
                            </div>
                            <div>
                                <Label>Entidade / comunidade</Label>
                                <Input value={form.entidade} onChange={(e) => setForm({ ...form, entidade: e.target.value })} placeholder="Ex: Associação X" data-testid="input-req-entidade" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Telefone</Label>
                                <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="input-req-tel" />
                            </div>
                            <div>
                                <Label>Vínculo *</Label>
                                <Select value={form.vinculo} onValueChange={(v) => setForm({ ...form, vinculo: v })}>
                                    <SelectTrigger data-testid="select-req-vinculo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {VINCULOS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Origem</Label>
                                <Input value={form.origem} onChange={(e) => setForm({ ...form, origem: e.target.value })} placeholder="Ex: Santarém - PA" data-testid="input-req-origem" />
                            </div>
                            <div>
                                <Label>Destino *</Label>
                                <Input value={form.destino} onChange={(e) => setForm({ ...form, destino: e.target.value })} placeholder="Ex: Belterra - PA" data-testid="input-req-destino" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Datas da viagem * <span className="text-xs text-muted-foreground font-normal">(Nº de dias = {(form.datas || []).filter(Boolean).length || 1})</span></Label>
                                <Button type="button" variant="outline" size="sm" onClick={addData} className="gap-1" data-testid="btn-add-data-req">
                                    <Plus className="w-3 h-3" /> Adicionar data
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {(form.datas || []).map((d, idx) => (
                                    <div key={idx} className="flex gap-2 items-center">
                                        <span className="text-xs text-muted-foreground w-6 text-center">{idx + 1}.</span>
                                        <Input type="date" value={d} onChange={(e) => changeData(idx, e.target.value)} data-testid={`input-req-data-${idx}`} />
                                        {(form.datas || []).length > 1 && (
                                            <Button type="button" variant="ghost" size="sm" onClick={() => removeData(idx)} data-testid={`btn-remove-data-req-${idx}`}>
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>Motivo da solicitação *</Label>
                            <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Descreva o motivo..." data-testid="input-req-motivo" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Tipo de veículo solicitado *</Label>
                                <Select value={form.tipo_veiculo} onValueChange={(v) => setForm({ ...form, tipo_veiculo: v })}>
                                    <SelectTrigger data-testid="select-req-tipo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {VEHICLE_TYPES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Nº de passageiros</Label>
                                <Input type="number" min="1" value={form.num_passageiros} onChange={(e) => setForm({ ...form, num_passageiros: e.target.value })} data-testid="input-req-passageiros" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Placa do veículo</Label>
                                <Input value={form.veiculo_placa} onChange={(e) => setForm({ ...form, veiculo_placa: e.target.value.toUpperCase() })} placeholder="Ex: ABC-1234" data-testid="input-req-placa" />
                            </div>
                            <div>
                                <Label>Motorista</Label>
                                <Input value={form.motorista_nome} onChange={(e) => setForm({ ...form, motorista_nome: e.target.value })} placeholder="Nome do motorista" data-testid="input-req-motorista" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Valor estimado (R$)</Label>
                                <Input type="number" step="0.01" value={form.valor_estimado} onChange={(e) => setForm({ ...form, valor_estimado: e.target.value })} placeholder="Ex: 500.00" data-testid="input-req-valor-estimado" />
                            </div>
                            <div>
                                <Label>Chave PIX</Label>
                                <Input value={form.chave_pix} onChange={(e) => setForm({ ...form, chave_pix: e.target.value })} placeholder="CPF, telefone, e-mail ou aleatória" data-testid="input-req-pix" />
                            </div>
                        </div>
                        <div>
                            <Label>Observações</Label>
                            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="input-req-obs" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        {editingId && (
                            <Button variant="secondary" className="gap-2" onClick={() => window.open(`/solicitacao/${editingId}/imprimir`, "_blank")} data-testid="btn-print-dialog-req">
                                <Printer className="w-4 h-4" /> Imprimir
                            </Button>
                        )}
                        <Button onClick={save} data-testid="btn-save-req">Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approve Dialog */}
            <Dialog open={!!approveDialog} onOpenChange={(v) => !v && setApproveDialog(null)}>
                <DialogContent className="max-w-2xl" data-testid="approve-dialog">
                    <DialogHeader>
                        <DialogTitle>Aprovar solicitação</DialogTitle>
                        <DialogDescription>Solicitação de {approveDialog?.solicitante_nome} para {approveDialog?.destino}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
                        <div>
                            <Label>Observações / condições da aprovação</Label>
                            <Textarea value={approveForm.observacoes_decisao} onChange={(e) => setApproveForm({ ...approveForm, observacoes_decisao: e.target.value })} data-testid="input-approve-obs" />
                        </div>
                        <div className="border-t border-border pt-4">
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="criar-viagem"
                                    checked={approveForm.criar_viagem}
                                    onCheckedChange={(v) => setApproveForm({ ...approveForm, criar_viagem: !!v })}
                                    data-testid="checkbox-criar-viagem"
                                />
                                <div>
                                    <label htmlFor="criar-viagem" className="font-medium cursor-pointer">Gerar viagem no sistema</label>
                                    <div className="text-xs text-muted-foreground">Cria uma viagem já vinculada a esta solicitação</div>
                                </div>
                            </div>
                            {approveForm.criar_viagem && (
                                <div className="mt-4 space-y-3 pl-6">
                                    <div>
                                        <Label>Tipo de viagem</Label>
                                        <Select value={approveForm.tipo_viagem} onValueChange={(v) => setApproveForm({ ...approveForm, tipo_viagem: v })}>
                                            <SelectTrigger data-testid="select-approve-tipo"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="fixo">Fixo</SelectItem>
                                                <SelectItem value="diaria">Diária</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>Responsável do veículo *</Label>
                                            <Input value={approveForm.responsavel_nome} onChange={(e) => setApproveForm({ ...approveForm, responsavel_nome: e.target.value })} data-testid="input-approve-resp" />
                                        </div>
                                        <div>
                                            <Label>CPF</Label>
                                            <Input value={approveForm.cpf} onChange={(e) => setApproveForm({ ...approveForm, cpf: e.target.value })} data-testid="input-approve-cpf" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>Modelo do veículo</Label>
                                            <Input value={approveForm.veiculo_modelo} onChange={(e) => setApproveForm({ ...approveForm, veiculo_modelo: e.target.value })} data-testid="input-approve-modelo" />
                                        </div>
                                        <div>
                                            <Label>Placa *</Label>
                                            <Input value={approveForm.veiculo_placa} onChange={(e) => setApproveForm({ ...approveForm, veiculo_placa: e.target.value.toUpperCase() })} data-testid="input-approve-placa" />
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Valor (R$)</Label>
                                        <Input type="number" step="0.01" value={approveForm.valor} onChange={(e) => setApproveForm({ ...approveForm, valor: e.target.value })} data-testid="input-approve-valor" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setApproveDialog(null)}>Cancelar</Button>
                        <Button onClick={doApprove} className="gap-2" data-testid="btn-confirm-approve">
                            <Check className="w-4 h-4" /> Aprovar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={!!rejectDialog} onOpenChange={(v) => !v && setRejectDialog(null)}>
                <DialogContent data-testid="reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Negar solicitação</DialogTitle>
                        <DialogDescription>Informe o motivo da negativa</DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label>Motivo</Label>
                        <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="input-reject-reason" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialog(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={doReject} data-testid="btn-confirm-reject">Negar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover solicitação?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta ação não pode ser desfeita. A solicitação de <strong>{confirmDelete?.solicitante_nome}</strong> será removida.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)} data-testid="btn-confirm-delete-req">Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
